import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AntCondition from '../../utils/AntCondition';
import AntLocalization from '../../utils/AntLocalization';
import AntAPI from '../../utils/AntAPI';
import './index.scss';

// ─── Status vocabulary (shared with AR voucher chain) ────────────────
// Tone determines stripe color + chip palette.
var STATUS_TONES = {
    New: 'neutral',
    Draft: 'neutral',
    Release: 'info',
    Released: 'info',
    Open: 'progress',
    Pending: 'progress',
    InProgress: 'progress',
    Matching: 'progress',
    ReEntry: 'warning',
    Warning: 'warning',
    Unpaid: 'warning',
    Close: 'success',
    Closed: 'success',
    Done: 'success',
    Paid: 'success',
    Reconciled: 'posted',
    Posted: 'posted',
    Approved: 'posted',
    Cancel: 'danger',
    Cancelled: 'danger',
    Canceled: 'danger',
    Rejected: 'danger',
    Failed: 'danger'
};

var STATUS_LABELS = {
    New: { vi: 'Mới', en: 'New', es: 'Nuevo', id: 'Baru', ja: '新規', zh: '新建', ko: '신규', hi: 'नया', ar: 'جديد', fr: 'Nouv.' },
    Draft: { vi: 'Nháp', en: 'Draft', fr: 'Brouill.', es: 'Borrador', id: 'Draf', ja: '下書き', zh: '草稿', ko: '초안', hi: 'ड्राफ़्ट', ar: 'مسودة' },
    Release: { vi: 'Đã duyệt', en: 'Released', fr: 'Validé', es: 'Aprobado', id: 'Disetujui', ja: '承認済み', zh: '已审核', ko: '승인됨', hi: 'स्वीकृत', ar: 'معتمد' },
    Released: { vi: 'Đã duyệt', en: 'Released', fr: 'Validé', es: 'Aprobado', id: 'Disetujui', ja: '承認済み', zh: '已审核', ko: '승인됨', hi: 'स्वीकृत', ar: 'معتمد' },
    Open: { vi: 'Đang xử lý', en: 'Open', fr: 'Traitement', es: 'Procesando', id: 'Memproses', ja: '処理中', zh: '处理中', ko: '처리 중', hi: 'प्रसंस्करण', ar: 'جاري المعالجة' },
    Pending: { vi: 'Chờ xử lý', en: 'Pending', fr: 'En attente', es: 'Pendiente', id: 'Tertunda', ja: '保留中', zh: '待处理', ko: '보류 중', hi: 'लंबित', ar: 'معلق' },
    InProgress: { vi: 'Đang xử lý', en: 'In progress', fr: 'Traitement', es: 'Procesando', id: 'Memproses', ja: '処理中', zh: '处理中', ko: '처리 중', hi: 'प्रसंस्करण', ar: 'جاري المعالجة' },
    Matching: { vi: 'Đang đối chiếu', en: 'Matching', fr: 'Rapprochement', es: 'Conciliando', id: 'Mencocokkan', ja: '照合中', zh: '对账中', ko: '대사 중', hi: 'मिलान जारी', ar: 'قيد المطابقة' },
    ReEntry: { vi: 'Nhập lại', en: 'Re-entry', fr: 'Ressaisie', es: 'Reingreso', id: 'Input ulang', ja: '再入力', zh: '重新录入', ko: '재입력', hi: 'पुनः दर्ज', ar: 'إعادة إدخال' },
    Close: { vi: 'Hoàn tất', en: 'Closed', fr: 'Fermé', es: 'Cerrado', id: 'Ditutup', ja: 'クローズ', zh: '已关闭', ko: '종료됨', hi: 'बंद', ar: 'مغلق' },
    Closed: { vi: 'Hoàn tất', en: 'Closed', fr: 'Fermé', es: 'Cerrado', id: 'Ditutup', ja: 'クローズ', zh: '已关闭', ko: '종료됨', hi: 'बंद', ar: 'مغلق' },
    Done: { vi: 'Hoàn tất', en: 'Done', fr: 'Terminé', es: 'Hecho', id: 'Selesai', ja: '完了', zh: '完成', ko: '완료', hi: 'संपन्न', ar: 'تم' },
    Paid: { vi: 'Đã thanh toán', en: 'Paid', fr: 'Payé', es: 'Pagado', id: 'Dibayar', ja: '支払済', zh: '已付款', ko: '결제됨', hi: 'भुगतान हुआ', ar: 'مدفوع' },
    Unpaid: { vi: 'Chưa thanh toán', en: 'Unpaid', fr: 'Impayé', es: 'No pagado', id: 'Belum dibayar', ja: '未払い', zh: '未付款', ko: '미결제', hi: 'भुगतान बाकी', ar: 'غير مدفوع' },
    Reconciled: { vi: 'Đã đối chiếu', en: 'Reconciled', fr: 'Rapproché', es: 'Conciliado', id: 'Direkonsiliasi', ja: '照合済み', zh: '已对账', ko: '대사 완료', hi: 'मिलान हुआ', ar: 'تمت المطابقة' },
    Posted: { vi: 'Đã hạch toán', en: 'Posted', fr: 'Comptabilisé', es: 'Contabilizado', id: 'Diposting', ja: '転記済み', zh: '已过账', ko: '전기됨', hi: 'पोस्ट किया गया', ar: 'تم الترحيل' },
    Approved: { vi: 'Đã duyệt', en: 'Approved', fr: 'Approuvé', es: 'Aprobado', id: 'Disetujui', ja: '承認済み', zh: '已批准', ko: '승인됨', hi: 'स्वीकृत', ar: 'معتمد' },
    Cancel: { vi: 'Đã huỷ', en: 'Cancelled', fr: 'Annulé', es: 'Cancelado', id: 'Dibatalkan', ja: 'キャンセル', zh: '已取消', ko: '취소됨', hi: 'रद्द', ar: 'ملغي' },
    Cancelled: { vi: 'Đã huỷ', en: 'Cancelled', fr: 'Annulé', es: 'Cancelado', id: 'Dibatalkan', ja: 'キャンセル', zh: '已取消', ko: '취소됨', hi: 'रद्द', ar: 'ملغي' },
    Canceled: { vi: 'Đã huỷ', en: 'Cancelled', fr: 'Annulé', es: 'Cancelado', id: 'Dibatalkan', ja: 'キャンセル', zh: '已取消', ko: '취소됨', hi: 'रद्द', ar: 'ملغي' },
    Rejected: { vi: 'Từ chối', en: 'Rejected', fr: 'Refusé', es: 'Rechazado', id: 'Ditolak', ja: '却下', zh: '已拒绝', ko: '거부됨', hi: 'अस्वीकृत', ar: 'مرفوض' },
    Failed: { vi: 'Thất bại', en: 'Failed', fr: 'Échec', es: 'Error', id: 'Gagal', ja: '失敗', zh: '失败', ko: '실패', hi: 'विफल', ar: 'فشل' }
};

function buildSearchConditions(sc) {
    var arr = [];
    if (!sc) return arr;
    Object.keys(sc).forEach(function (k) { if (sc[k] !== undefined) arr.push({ name: k, value: sc[k] }); });
    return arr;
}

function applyDrillDown(template, row) {
    if (!template || !row) return null;
    return template.replace(/:(\w+)/g, function (m, key) { return (row && row[key] != null) ? row[key] : ''; });
}

function getCurrencyFormatter() {
    try {
        var opts = AntLocalization.formatCurrency(false);
        var lang = opts.language || 'en-US';
        var nfOpts = { signDisplay: opts.signDisplay };
        if (opts.currency) {
            nfOpts.style = 'currency';
            nfOpts.currency = opts.currency;
            nfOpts.currencyDisplay = 'narrowSymbol';
        }
        var nf = new Intl.NumberFormat(lang, nfOpts);
        // VND hiển thị "đ" đặt sau số như AntLocalization.translateCurrency (Intl trả "₫")
        if (opts.currency === 'VND') return { format: function (n) { return nf.format(n).replace('₫', '').trim() + ' đ'; } };
        return nf;
    } catch (e) {
        return new Intl.NumberFormat('en-US');
    }
}

function getNumberFormatter() {
    try {
        var opts = AntLocalization.formatCurrency(false);
        return new Intl.NumberFormat(opts.language || 'en-US');
    } catch (e) {
        return new Intl.NumberFormat('en-US');
    }
}

function toDate(value) {
    if (value == null || value === '') return null;
    var n = (typeof value === 'number') ? value : Number(value);
    if (!isNaN(n) && n > 0) {
        // BE trả Unix seconds → < 1e12; ms → >= 1e12
        var ms = n < 1e12 ? n * 1000 : n;
        var d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
    }
    var d2 = new Date(String(value));
    return isNaN(d2.getTime()) ? null : d2;
}

var PLACE_TYPE_LABELS = {
    Table: { vi: 'Bàn', en: 'Table', fr: 'Table', es: 'Mesa', id: 'Meja', ja: 'テーブル', zh: '桌', ko: '테이블', hi: 'टेबल', ar: 'طاولة' },
    Room: { vi: 'Phòng', en: 'Room', fr: 'Salle', id: 'Ruang', ja: '部屋', zh: '房间', ko: '룸', hi: 'कक्ष', ar: 'قاعة', es: 'Sala' },
    Seat: { vi: 'Ghế', en: 'Seat', fr: 'Siège', es: 'Asiento', id: 'Kursi', ja: '席', zh: '座位', ko: '좌석', hi: 'सीट', ar: 'المقعد' },
    Field: { vi: 'Sân', en: 'Field', fr: 'Terrain', es: 'Cancha', id: 'Lapangan', zh: '场地', ko: '구장', hi: 'मैदान', ar: 'ملعب', ja: 'コート' },
    Court: { vi: 'Sân', en: 'Court', fr: 'Terrain', es: 'Cancha', id: 'Lapangan', ja: 'コート', zh: '场地', ko: '구장', hi: 'मैदान', ar: 'ملعب' },
    Bed: { vi: 'Giường', en: 'Bed', fr: 'Lit', es: 'Cama', id: 'Ranjang', ja: 'ベッド', zh: '床', ko: '침대', hi: 'बिस्तर', ar: 'سرير' },
    Vehicle: { vi: 'Xe', en: 'Vehicle', fr: 'Véhicule', es: 'Vehículo', id: 'Kendaraan', ja: '車両', zh: '车辆', ko: '차량', hi: 'वाहन', ar: 'مركبة' },
    Car: { vi: 'Xe', en: 'Car', fr: 'Auto', es: 'Auto', id: 'Mobil', ja: '車', zh: '车', ko: '차', hi: 'कार', ar: 'سيارة' }
};

function getPlacePrefix(typeCode) {
    if (!typeCode) return '';
    var def = PLACE_TYPE_LABELS[typeCode];
    return def ? AntLocalization.translateText(def) : String(typeCode);
}

function formatCell(value, col, formatters) {
    if (value === null || value === undefined || value === '') return '';
    var fmt = col && col.format;
    if (fmt && fmt.type === 'currency') {
        var n = Number(value); if (isNaN(n)) return String(value);
        return formatters.currency.format(n);
    }
    if (fmt && fmt.type === 'number') {
        var n2 = Number(value); if (isNaN(n2)) return String(value);
        return formatters.number.format(n2);
    }
    if ((col && (col.dataType === 'date' || col.dataType === 'datetime')) || (fmt && (fmt.type === 'date' || fmt.type === 'datetime'))) {
        var d = toDate(value);
        if (!d) return String(value);
        var dateOnly = (col && col.dataType === 'date') || (fmt && fmt.type === 'date');
        if (dateOnly) return d.toLocaleDateString();
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return String(value);
}

function isStatusColumn(c) {
    return !!(c && c.dataField && /_status$/.test(c.dataField));
}

function isAmountColumn(c) {
    return !!(c && c.format && (c.format.type === 'currency' || c.format.type === 'number'));
}

function isDateColumn(c) {
    if (c && c.format && (c.format.type === 'date' || c.format.type === 'datetime')) return true;
    if (c && (c.dataType === 'date' || c.dataType === 'datetime')) return true;
    if (c && c.dataField && /_at$|_date$/.test(c.dataField)) return true;
    return false;
}

function classifyColumns(columns) {
    var codeCol = null, statusCol = null, amountCol = null, dateCol = null, primaryCols = [];
    (columns || []).forEach(function (c) {
        if (!codeCol && c.dataField === 'code') { codeCol = c; return; }
        if (!statusCol && isStatusColumn(c)) { statusCol = c; return; }
        if (!amountCol && isAmountColumn(c)) { amountCol = c; return; }
        if (!dateCol && isDateColumn(c)) { dateCol = c; return; }
        primaryCols.push(c);
    });
    return { codeCol: codeCol, statusCol: statusCol, amountCol: amountCol, dateCol: dateCol, primaryCols: primaryCols };
}

function getStatusLabel(code) {
    if (!code) return null;
    var def = STATUS_LABELS[code];
    if (def) return AntLocalization.translateText(def);
    return String(code);
}

function getStatusTone(code) {
    if (!code) return 'neutral';
    return STATUS_TONES[code] || 'neutral';
}

export default function AntHomeMiniGrid(props) {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    var visible = AntCondition.isVisible(null, props);
    var refreshSec = props.refreshIntervalSec == null ? 60 : props.refreshIntervalSec;
    var pageSize = props.pageSize || 5;
    var emptyMode = props.emptyState || 'show';
    var seeAllPath = props.seeAllPath || props.viewAllPath;

    var apiInstance = useMemo(function () {
        if (props.API) return props.API;
        if (props.api && props.api.module && props.api.table) return new AntAPI(props.api.module, props.api.table);
        return null;
    }, [props.API, props.api && props.api.module, props.api && props.api.table]);

    var apiKey = props.api ? (props.api.module + ':' + props.api.table) : (props.API && props.API.entityName) || '';
    var scKey = JSON.stringify(props.searchConditions || {});
    var sortKey = JSON.stringify(props.sortBy || []);

    var formatters = useMemo(function () {
        return { currency: getCurrencyFormatter(), number: getNumberFormatter() };
    }, []);

    var classified = useMemo(function () { return classifyColumns(props.columns || []); }, [JSON.stringify(props.columns || [])]);

    useEffect(function () {
        if (!visible || !apiInstance) return;
        var cancelled = false;

        async function load(showSpinner) {
            if (showSpinner) setLoading(true);
            try {
                var params = buildSearchConditions(props.searchConditions);
                if (props.sortBy && props.sortBy.length > 0) params.push({ name: 'ordering', value: props.sortBy });
                params.push({ name: 'pageSize', value: pageSize });
                params.push({ name: 'pageIndex', value: 0 });
                var res = await apiInstance.getList(params, null, { byPassTotal: true });
                if (cancelled) return;
                setRows((res && res.rows) || []);
                setError(false);
            } catch (e) {
                if (!cancelled) { setRows([]); setError(true); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load(true);

        if (refreshSec > 0) {
            var iv = setInterval(function () { load(false); }, refreshSec * 1000);
            return function () { cancelled = true; clearInterval(iv); };
        }
        return function () { cancelled = true; };
    }, [visible, apiInstance, apiKey, scKey, sortKey, pageSize, refreshSec]);

    if (!visible) return null;
    if (!apiInstance) return null;

    var isEmpty = !loading && (!rows || rows.length === 0);
    if (!loading && isEmpty && emptyMode === 'collapse') return null;

    function onRowClick(row) {
        if (props.drillDown) {
            var path = applyDrillDown(props.drillDown, row);
            if (path) { navigate(path); return; }
        }
        if (seeAllPath) navigate(seeAllPath);
    }

    var codeCol = classified.codeCol;
    var statusCol = classified.statusCol;
    var amountCol = classified.amountCol;
    var dateCol = classified.dateCol;
    var primaryCols = classified.primaryCols;

    return (
        <div className="ant-home-minigrid">
            <div className="ant-home-minigrid__header">
                <div className="ant-home-minigrid__title">{AntLocalization.translateText(props.title)}</div>
                {seeAllPath && (
                    <button type="button" className="ant-home-minigrid__viewall" onClick={function () { navigate(seeAllPath); }}>
                        {AntLocalization.translateText(props.viewAllLabel || { vi: 'Xem tất cả', en: 'View all', fr: 'Voir tout', es: 'Ver todo', id: 'Lihat semua', ja: 'すべて表示', zh: '查看全部', ko: '전체 보기', hi: 'सभी देखें', ar: 'عرض الكل' })} →
                    </button>
                )}
            </div>

            <ul className="ant-home-minigrid__cards">
                {loading && Array.from({ length: pageSize }).map(function (_, i) {
                    return (
                        <li key={'sk' + i} className="ant-home-minigrid__card ant-home-minigrid__card--skeleton" aria-hidden="true">
                            <div className="ant-home-minigrid__card-body">
                                <div className="ant-home-minigrid__card-top">
                                    <div className="ant-home-minigrid__main">
                                        <span className="sk-bar sk-bar--long"></span>
                                    </div>
                                    <span className="sk-bar sk-bar--code"></span>
                                </div>
                                <div className="ant-home-minigrid__card-bottom">
                                    <span className="sk-bar sk-bar--code"></span>
                                    <span className="sk-bar sk-bar--chip"></span>
                                    <span className="sk-bar sk-bar--code"></span>
                                </div>
                            </div>
                        </li>
                    );
                })}

                {!loading && isEmpty && (
                    <li className="ant-home-minigrid__empty">
                        {error
                            ? AntLocalization.translateText({ vi: 'Không tải được dữ liệu', en: 'Failed to load', fr: 'Échec du chargement', es: 'Error al cargar datos', id: 'Gagal memuat data', ja: 'データを読み込めません', zh: '无法加载数据', ko: '데이터 로드 실패', hi: 'डेटा लोड नहीं हो सका', ar: 'تعذر تحميل البيانات' })
                            : AntLocalization.translateText({ vi: 'Không có dữ liệu', en: 'No data', fr: 'Aucune donnée', es: 'Sin datos', id: 'Tidak ada data', ja: 'データなし', zh: '暂无数据', ko: '데이터 없음', hi: 'कोई डेटा नहीं', ar: 'لا توجد بيانات' })}
                    </li>
                )}

                {!loading && !isEmpty && rows.map(function (r, ri) {
                    var statusCode = statusCol ? r[statusCol.dataField] : null;
                    var tone = getStatusTone(statusCode);
                    var codeText = codeCol ? formatCell(r[codeCol.dataField], codeCol, formatters) : null;
                    var amountText = amountCol ? formatCell(r[amountCol.dataField], amountCol, formatters) : null;
                    var dateText = dateCol ? formatCell(r[dateCol.dataField], dateCol, formatters) : null;
                    var statusLabel = getStatusLabel(statusCode);

                    // Distinguish place fields from other primary fields so the place
                    // can render as a chip (mirrors BrowsePanel.is-place styling).
                    var placeTexts = [];
                    var otherTexts = [];
                    primaryCols.forEach(function (c) {
                        var txt = formatCell(r[c.dataField], c, formatters);
                        if (txt == null || txt === '') return;
                        if (c.dataField === 'serve_place_name' || c.dataField === 'serve_place_names') {
                            var prefix = getPlacePrefix(r.serve_place_type);
                            placeTexts.push(prefix ? prefix + ' ' + txt : txt);
                        } else {
                            otherTexts.push(txt);
                        }
                    });
                    var placeText = placeTexts.join(' · ');
                    var otherText = otherTexts.join(' · ');
                    var titleText, subtitleText, titleIsPlace;
                    if (placeText) {
                        titleText = placeText;
                        subtitleText = otherText;
                        titleIsPlace = true;
                    } else {
                        titleText = otherText;
                        subtitleText = '';
                        titleIsPlace = false;
                    }
                    var showCodeInMeta = codeText != null && codeText !== '';

                    return (
                        <li key={r.id || ri}
                            className="ant-home-minigrid__card"
                            onClick={function () { onRowClick(r); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r); } }}>
                            <div className="ant-home-minigrid__card-body">
                                <div className="ant-home-minigrid__card-top">
                                    <div className="ant-home-minigrid__main">
                                        {titleText && (
                                            <div className={'ant-home-minigrid__primary' + (titleIsPlace ? ' is-place' : '')} title={titleText}>
                                                {titleText}
                                            </div>
                                        )}
                                        {subtitleText && (
                                            <div className="ant-home-minigrid__subtitle" title={subtitleText}>
                                                {subtitleText}
                                            </div>
                                        )}
                                    </div>
                                    {amountText && <div className="ant-home-minigrid__amount" title={amountText}>{amountText}</div>}
                                </div>
                                {(statusLabel || showCodeInMeta || dateText) && (
                                    <div className="ant-home-minigrid__card-bottom">
                                        <span className="ant-home-minigrid__date">{dateText || ''}</span>
                                        <span className={'ant-home-minigrid__chip tone-' + tone}>{statusLabel || ''}</span>
                                        <span className="ant-home-minigrid__code" title={codeText || ''}>{showCodeInMeta ? codeText : ''}</span>
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
